/**
 * Hash Trigger
 *
 * Opens popup when the URL contains a specific hash fragment.
 * Supports:
 * - Initial page load with hash
 * - Hash changes via browser navigation (back/forward)
 * - Programmatic hash changes
 * - Clears hash after popup opens using replaceState to avoid back button issues
 * - Proximity-based preloading for links with href ending in the hash
 */

import type { Popup } from '../popup';
import { ProximityPreloadStrategy } from '../preload-strategies/proximity-strategy';
import { createLogger } from '../../shared/logger';
import { TriggerStateBroadcaster } from './trigger-state-broadcaster';

const logger = createLogger('HashTrigger');

export class HashTrigger {
	private popup: Popup;
	private hashValue: string;
	private boundHashChangeHandler: () => void;
	private boundPopupClosedHandler: (event: Event) => void;
	private preloadStrategy: ProximityPreloadStrategy | null = null;
	private broadcaster: TriggerStateBroadcaster;

	constructor(popup: Popup, hashValue: string) {
		logger.log('constructor - hashValue:', hashValue);
		this.popup = popup;
		// Normalize: remove leading # if present
		this.hashValue = hashValue.replace(/^#/, '');
		this.boundHashChangeHandler = this.handleHashChange.bind(this);
		this.boundPopupClosedHandler = this.handlePopupClosed.bind(this);
		this.broadcaster = new TriggerStateBroadcaster(
			popup.getFeedId(),
			'hash'
		);
	}

	/**
	 * Initialize the trigger
	 */
	public init(): void {
		logger.log('init() called, watching for hash:', this.hashValue);

		if (!this.hashValue) {
			logger.log('No hash value provided, skipping');
			return;
		}

		// Broadcast initial waiting state
		this.broadcaster.setState('waiting');
		this.broadcaster.setProgress({ hash: `#${this.hashValue}` });

		// Set up proximity-based preloading for links with href ending in the hash
		// This allows preloading when user hovers near a link like <a href="#contact-form">
		const hrefSelector = `[href$="#${this.hashValue}"]`;
		logger.log('Setting up proximity preload for selector:', hrefSelector);
		this.preloadStrategy = new ProximityPreloadStrategy(hrefSelector, 100);
		this.preloadStrategy.init(() => {
			logger.log('Proximity preload triggered for hash link');
			this.broadcaster.setState('preloading');
			this.popup.preload();
		});

		// Listen for hash changes (browser navigation, programmatic changes)
		window.addEventListener('hashchange', this.boundHashChangeHandler);

		// Listen for popup close to reset preload strategy
		// Only if respectDismissal is false (dismissal not remembered)
		if (!this.popup.getRespectsDismisal()) {
			document.addEventListener(
				'gp_popup_closed',
				this.boundPopupClosedHandler
			);
		}

		// Check initial hash on page load
		this.checkCurrentHash();
	}

	/**
	 * Destroy the trigger
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.broadcaster.destroy();
		window.removeEventListener('hashchange', this.boundHashChangeHandler);
		document.removeEventListener(
			'gp_popup_closed',
			this.boundPopupClosedHandler
		);
		this.preloadStrategy?.destroy();
	}

	/**
	 * Handle popup closed event - reset preload strategy for re-triggering
	 */
	private handlePopupClosed(event: Event): void {
		const customEvent = event as CustomEvent;
		if (customEvent.detail?.feedId === this.popup.getFeedId()) {
			logger.log('Popup closed, resetting preload strategy');
			this.preloadStrategy?.reset();
		}
	}

	/**
	 * Handle hash change events
	 */
	private handleHashChange(): void {
		logger.log('Hash changed, checking...');
		this.checkCurrentHash();
	}

	/**
	 * Check if current URL hash matches our target
	 */
	private checkCurrentHash(): void {
		const currentHash = window.location.hash.replace(/^#/, '');

		logger.log('Checking hash:', currentHash, 'vs target:', this.hashValue);

		if (this.matchesHash(currentHash)) {
			logger.log('Hash matches! Showing popup');
			this.broadcaster.setState('triggered');

			// Preload and show
			this.popup.preload();
			this.popup.show();

			// Clear the hash to allow re-triggering and clean up the URL
			// Using replaceState prevents back button from re-triggering the popup
			this.clearHash();
		}
	}

	/**
	 * Check if the current hash matches our target value.
	 * Case-insensitive for user convenience.
	 */
	private matchesHash(currentHash: string): boolean {
		// Case-insensitive match
		return currentHash.toLowerCase() === this.hashValue.toLowerCase();
	}

	/**
	 * Clear the hash from the URL without triggering a page reload.
	 * Uses replaceState to avoid adding to browser history, which prevents
	 * the back button from re-triggering the popup.
	 */
	private clearHash(): void {
		if (window.history && window.history.replaceState) {
			const url = window.location.pathname + window.location.search;
			window.history.replaceState(null, '', url);
			logger.log('Hash cleared from URL');
		}
	}
}
